/**
 * The five cell bodies (CLAUDE.md 21.2, ADR-0007).
 *
 * Each renders its own content and, where it has one, its result. The result sits
 * inline with the expression rather than in a separate column, because that is how
 * the calculation reads on paper and how Mathcad shows it -- a value in a distant
 * column is a value nobody checks against the formula that produced it.
 */
import { useId, useRef, useState, type ClipboardEvent, type DragEvent } from 'react'
import { ImagePlus, Loader2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { useBlobUrl } from '@/components/support/useBlobUrl'
import { formatNumber } from '@/lib/format'
import { cn } from '@/lib/cn'
import { MathField } from './MathField'
import { SHEET_IMAGE_TYPES, sheetAssetFilePath, uploadSheetAsset, type SheetAsset } from '../api'
import type { CellResult, ImageCell, MathCell, PlotCell, TableCell, TextCell } from '../model/types'

/** Enough figures to be useful without implying precision the input never had. */
function decimalsFor(value: number): number {
  const magnitude = Math.abs(value)
  return magnitude === 0 ? 2 : magnitude < 1 ? 4 : magnitude < 1000 ? 3 : 1
}

/**
 * A computed value, scalar or matrix. Units are notation and never translated
 * (14.4); the decimal separator is always a dot, in every locale (ADR-0004).
 */
export function ResultValue({ result }: { result: CellResult | undefined }) {
  if (result?.matrix) {
    const { rows, unit } = result.matrix
    return (
      <span className="ms-2 inline-flex items-center gap-1.5 align-middle">
        {/* Drawn brackets rather than a border on the table: a matrix should read
            as one quantity, and a boxed grid reads as a data table. */}
        <span className="text-lg text-text-muted" aria-hidden>
          [
        </span>
        <span className="inline-grid gap-x-3 gap-y-0.5">
          {rows.map((row, i) => (
            <span key={i} className="flex gap-3">
              {row.map((cellValue, j) => (
                <span key={j} className="tabular text-sm font-semibold">
                  {formatNumber(cellValue, { decimals: decimalsFor(cellValue) })}
                </span>
              ))}
            </span>
          ))}
        </span>
        <span className="text-lg text-text-muted" aria-hidden>
          ]
        </span>
        {unit && <span className="text-sm text-text-muted">{unit}</span>}
      </span>
    )
  }

  if (!result?.value) return null
  const { value, unit } = result.value

  return (
    <span className="ms-2 whitespace-nowrap">
      <span className="font-semibold">{formatNumber(value, { decimals: decimalsFor(value) })}</span>
      {unit && <span className="ms-1 text-sm text-text-muted">{unit}</span>}
    </span>
  )
}

export function ErrorDetail({ result }: { result: CellResult | undefined }) {
  const { t } = useTranslation('sheets')
  if (!result?.error) return null
  const { kind, messageKey, params } = result.error

  return (
    <div className="mt-1.5 rounded-md border border-[var(--status-warn)] bg-surface p-3">
      <p className="text-sm font-semibold text-[var(--status-warn)]">
        {t(`sheets.error.title.${kind}`)}
      </p>
      <p className="mt-1 text-sm leading-relaxed text-text">
        {t(messageKey, { ...(params ?? {}) })}
      </p>
    </div>
  )
}

export function MathCellBody({
  cell,
  result,
  onChange,
  onFocus,
}: {
  cell: MathCell
  result: CellResult | undefined
  onChange: (latex: string) => void
  onFocus: () => void
}) {
  const { t } = useTranslation('sheets')
  return (
    <div>
      <div className="flex flex-wrap items-baseline">
        <MathField
          value={cell.latex}
          onChange={onChange}
          onFocus={onFocus}
          placeholder={t('sheets.cell.empty_math')}
          ariaLabel={t('sheets.insert.math')}
        />
        <ResultValue result={result} />
      </div>
      <ErrorDetail result={result} />
    </div>
  )
}

export function TextCellBody({
  cell,
  onChange,
}: {
  cell: TextCell
  onChange: (html: string) => void
}) {
  const { t } = useTranslation('sheets')
  return (
    <textarea
      value={cell.html}
      onChange={(event) => onChange(event.target.value)}
      placeholder={t('sheets.cell.empty_text')}
      aria-label={t('sheets.insert.text')}
      rows={Math.max(2, cell.html.split('\n').length)}
      className="w-full resize-none border-0 bg-transparent p-0 text-sm leading-relaxed text-text placeholder:text-text-muted focus:outline-none"
    />
  )
}

export function TableCellBody({
  cell,
  onChange,
}: {
  cell: TableCell
  onChange: (next: TableCell) => void
}) {
  const { t } = useTranslation('sheets')

  const setRow = (index: number, patch: Partial<TableCell['rows'][number]>) => {
    onChange({
      ...cell,
      rows: cell.rows.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    })
  }

  return (
    <div className="scroll-x">
      <input
        value={cell.caption}
        onChange={(event) => onChange({ ...cell, caption: event.target.value })}
        aria-label={t('sheets.table.caption')}
        className="mb-2 w-full border-0 bg-transparent p-0 text-[11px] font-semibold uppercase tracking-wider text-text-muted focus:outline-none"
      />
      <table className="w-full max-w-lg border-collapse text-sm">
        <thead>
          <tr className="text-[11px] uppercase tracking-wider text-text-muted">
            <th className="pb-1 text-start font-semibold">{t('sheets.table.symbol')}</th>
            <th className="pb-1 text-end font-semibold">{t('sheets.table.value')}</th>
            <th className="pb-1 ps-3 text-start font-semibold">{t('sheets.table.unit')}</th>
          </tr>
        </thead>
        <tbody>
          {cell.rows.map((row, index) => (
            <tr key={index} className="border-t border-border">
              <td className="py-1 pe-3">
                <input
                  value={row.symbol}
                  onChange={(event) => setRow(index, { symbol: event.target.value })}
                  aria-label={t('sheets.table.symbol')}
                  className="w-24 border-0 bg-transparent p-0 font-mono text-sm focus:outline-none"
                />
              </td>
              <td className="py-1">
                <input
                  value={row.value ?? ''}
                  inputMode="decimal"
                  onChange={(event) =>
                    setRow(index, {
                      value: event.target.value === '' ? null : Number(event.target.value),
                    })
                  }
                  aria-label={t('sheets.table.value')}
                  className="tabular w-28 border-0 bg-transparent p-0 text-end text-sm focus:outline-none"
                />
              </td>
              <td className="py-1 ps-3">
                <input
                  value={row.unit}
                  onChange={(event) => setRow(index, { unit: event.target.value })}
                  aria-label={t('sheets.table.unit')}
                  className="w-20 border-0 bg-transparent p-0 text-sm text-text-muted focus:outline-none"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        onClick={() =>
          onChange({ ...cell, rows: [...cell.rows, { symbol: '', value: null, unit: '' }] })
        }
        className="mt-2 text-xs font-medium text-accent hover:underline"
      >
        {t('sheets.table.add_row')}
      </button>
    </div>
  )
}

/**
 * The plot. Drawn with our own SVG rather than a chart library, so it inherits the
 * theme tokens and matches the module diagrams (CLAUDE.md 8.3).
 */
export function PlotCellBody({
  cell,
  result,
  onChange,
}: {
  cell: PlotCell
  result: CellResult | undefined
  onChange: (next: PlotCell) => void
}) {
  const { t } = useTranslation('sheets')
  const series = result?.series ?? []

  const xs = series.map(([x]) => x)
  const ys = series.map(([, y]) => y)
  const minX = xs.length ? Math.min(...xs) : 0
  const maxX = xs.length ? Math.max(...xs) : 1
  const minY = ys.length ? Math.min(0, ...ys) : 0
  const maxY = ys.length ? Math.max(...ys) : 1
  const spanX = maxX - minX || 1
  const spanY = maxY - minY || 1

  const px = (x: number) => 44 + ((x - minX) / spanX) * 556
  const py = (y: number) => 160 - ((y - minY) / spanY) * 140

  const path = series
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${px(x).toFixed(1)} ${py(y).toFixed(1)}`)
    .join(' ')

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <input
          value={cell.caption}
          onChange={(event) => onChange({ ...cell, caption: event.target.value })}
          aria-label={t('sheets.plot.caption')}
          className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[11px] font-semibold uppercase tracking-wider text-text-muted focus:outline-none"
        />
        <label className="flex items-center gap-1.5 text-xs text-text-muted">
          {t('sheets.plot.sweep')}
          <input
            value={cell.sweep}
            onChange={(event) => onChange({ ...cell, sweep: event.target.value })}
            aria-label={t('sheets.plot.sweep')}
            className="w-16 rounded border border-border bg-surface px-1.5 py-0.5 font-mono text-xs text-text focus:outline-none"
          />
        </label>
        {(['from', 'to', 'steps'] as const).map((field) => (
          <label key={field} className="flex items-center gap-1.5 text-xs text-text-muted">
            {t(`sheets.plot.${field}`)}
            <input
              value={cell[field]}
              inputMode="decimal"
              onChange={(event) => onChange({ ...cell, [field]: Number(event.target.value) })}
              aria-label={t(`sheets.plot.${field}`)}
              className="tabular w-14 rounded border border-border bg-surface px-1.5 py-0.5 text-xs text-text focus:outline-none"
            />
          </label>
        ))}
      </div>

      <MathField
        value={cell.latex}
        onChange={(latex) => onChange({ ...cell, latex })}
        ariaLabel={t('sheets.plot.caption')}
        className="mb-2"
      />

      <div className="scroll-x">
        <svg
          viewBox="0 0 620 190"
          className="block w-full max-w-2xl"
          role="img"
          aria-label={cell.caption || t('sheets.plot.caption')}
        >
          <line x1="44" y1="160" x2="600" y2="160" stroke="var(--diagram-axis)" strokeWidth="1.2" />
          <line x1="44" y1="14" x2="44" y2="160" stroke="var(--diagram-axis)" strokeWidth="1.2" />
          {[0, 0.25, 0.5, 0.75, 1].map((f) => (
            <line
              key={f}
              x1="44"
              y1={160 - f * 140}
              x2="600"
              y2={160 - f * 140}
              stroke="var(--diagram-grid)"
              strokeWidth="1"
            />
          ))}
          {path && (
            <path
              d={path}
              fill="none"
              stroke="var(--diagram-moment)"
              strokeWidth="2.1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
          <text x="44" y="178" className="fill-[var(--text-muted)] text-[10px]">
            {formatNumber(minX, { decimals: 1 })}
          </text>
          <text x="600" y="178" textAnchor="end" className="fill-[var(--text-muted)] text-[10px]">
            {formatNumber(maxX, { decimals: 1 })}
          </text>
          <text x="40" y="24" textAnchor="end" className="fill-[var(--text-muted)] text-[10px]">
            {formatNumber(maxY, { decimals: 1 })}
          </text>
        </svg>
      </div>

      {/* A diagram carries a text alternative and a data fallback (14.5). */}
      {series.length > 0 && (
        <p className="mt-1 text-xs text-text-muted">
          {formatNumber(minX, { decimals: 1 })} → {formatNumber(maxX, { decimals: 1 })},{' '}
          {formatNumber(minY, { decimals: 1 })} → {formatNumber(maxY, { decimals: 1 })}
        </p>
      )}
      <ErrorDetail result={result} />
    </div>
  )
}

/* --- image ------------------------------------------------------------------------ */

const IMAGE_EXTENSION_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
}

/** The browser's idea of the type, falling back to the extension for pasted blobs.
 *  A courtesy check only: the server sniffs the bytes and decides for itself. */
function guessImageType(file: File): string {
  if (file.type) return file.type
  const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
  return IMAGE_EXTENSION_TYPES[extension] ?? ''
}

/** The first image file in a paste, if any. A pasted screenshot arrives unnamed. */
function pastedImage(event: ClipboardEvent): File | null {
  for (const item of Array.from(event.clipboardData?.items ?? [])) {
    if (item.kind !== 'file') continue
    const file = item.getAsFile()
    if (!file) continue
    if (file.name && file.name !== 'image.png') return file
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
    const extension = file.type.split('/')[1] || 'png'
    return new File([file], `pasted-${stamp}.${extension}`, { type: file.type })
  }
  return null
}

function ImagePlaceholder({ children }: { children: string }) {
  return (
    <div
      className={cn(
        'flex min-h-24 max-w-md items-center justify-center rounded-md p-3 text-center',
        'border border-dashed border-[var(--border-strong)] text-xs text-text-muted',
      )}
    >
      {children}
    </div>
  )
}

/**
 * Pick, drop or paste an image; upload it; hand back the asset.
 *
 * The visible control is a real button that opens the hidden file input, so focus
 * stays on something a paste can land on: click "Choose an image", cancel the
 * picker, press Ctrl+V. Refusals by type happen here first as a courtesy; the server
 * repeats every check from the bytes (docs/SHEETS.md 4.4).
 */
function ImageDropZone({
  sheetId,
  onUploaded,
}: {
  sheetId: number
  onUploaded: (asset: SheetAsset) => void
}) {
  const { t } = useTranslation('sheets')
  const inputId = useId()
  const input = useRef<HTMLInputElement | null>(null)
  const [dragging, setDragging] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const accept = SHEET_IMAGE_TYPES.join(',')

  const send = async (file: File | null | undefined) => {
    if (!file || busy) return
    if (!(SHEET_IMAGE_TYPES as readonly string[]).includes(guessImageType(file))) {
      setError(t('sheets.image.rejected_type', { name: file.name }))
      return
    }
    setBusy(true)
    setError(null)
    try {
      onUploaded(await uploadSheetAsset(sheetId, file))
    } catch (err) {
      setError(err instanceof ApiError ? err.localised : t('sheets.image.upload_failed'))
    } finally {
      setBusy(false)
    }
  }

  const onDrop = (event: DragEvent) => {
    event.preventDefault()
    setDragging(false)
    void send(event.dataTransfer.files[0])
  }

  return (
    <div
      data-testid="image-drop-zone"
      className={cn(
        'max-w-md space-y-1.5 rounded-md border border-dashed p-3 transition-colors',
        dragging ? 'border-accent bg-[var(--accent-subtle)]' : 'border-[var(--border-strong)]',
      )}
      onDragOver={(event) => {
        event.preventDefault()
        setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      onPaste={(event) => {
        const file = pastedImage(event)
        if (!file) return
        event.preventDefault()
        void send(file)
      }}
    >
      <input
        ref={input}
        id={inputId}
        type="file"
        accept={accept}
        tabIndex={-1}
        className="sr-only"
        disabled={busy}
        onChange={(event) => {
          void send(event.target.files?.[0])
          event.target.value = ''
        }}
      />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => input.current?.click()}
          disabled={busy}
          className="inline-flex h-9 items-center gap-2 rounded-md border border-border bg-surface px-3 text-sm text-text hover:bg-surface-raised disabled:opacity-50"
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <ImagePlus className="h-3.5 w-3.5" aria-hidden />
          )}
          {busy ? t('sheets.image.uploading') : t('sheets.image.choose')}
        </button>
        <span className="text-xs text-text-muted">{t('sheets.image.drop_hint')}</span>
      </div>
      <p className="text-xs text-text-muted">{t('sheets.image.formats')}</p>
      {error && (
        <p role="alert" className="text-xs text-[var(--status-fail)]">
          {error}
        </p>
      )}
    </div>
  )
}

/**
 * An uploaded image, fetched with the bearer token into a `blob:` URL. The
 * document holds only the asset id (docs/SHEETS.md 4.4); the endpoint that turns
 * it into bytes answers with a sandbox header, and a plain `<img src>` would carry
 * neither the token nor that contract.
 */
function StoredImage({
  cell,
  sheetId,
  assetId,
  readOnly,
  onChange,
}: {
  cell: ImageCell
  sheetId: number | null
  assetId: number
  readOnly: boolean
  onChange: (next: ImageCell) => void
}) {
  const { t } = useTranslation('sheets')
  const blob = useBlobUrl(sheetId === null ? null : sheetAssetFilePath(sheetId, assetId))

  const setWidth = (raw: string) => {
    if (raw === '') {
      const { widthPx: _dropped, ...rest } = cell
      onChange(rest)
      return
    }
    const value = Math.round(Number(raw))
    if (Number.isFinite(value) && value > 0) onChange({ ...cell, widthPx: value })
  }

  return (
    <figure className="m-0">
      {blob.status === 'ready' && (
        <img
          src={blob.url}
          alt={cell.alt}
          style={cell.widthPx ? { width: `${cell.widthPx}px` } : undefined}
          className="block h-auto max-w-full rounded-md border border-border"
        />
      )}
      {blob.status === 'loading' && (
        <div
          aria-busy="true"
          aria-label={t('sheets.cell.evaluating')}
          className="h-24 w-40 animate-pulse rounded-md bg-surface-raised"
        />
      )}
      {blob.status === 'error' && (
        <div className="space-y-2">
          <ImagePlaceholder>{t('sheets.image.unavailable')}</ImagePlaceholder>
          {/* The file is gone but the cell is not: offer to put a new one in its place. */}
          {!readOnly && sheetId !== null && (
            <ImageDropZone
              sheetId={sheetId}
              onUploaded={(asset) => onChange({ ...cell, assetId: asset.id })}
            />
          )}
        </div>
      )}

      {readOnly ? (
        cell.alt && <figcaption className="mt-1 text-xs text-text-muted">{cell.alt}</figcaption>
      ) : (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            value={cell.alt}
            onChange={(event) => onChange({ ...cell, alt: event.target.value })}
            aria-label={t('sheets.image.alt')}
            placeholder={t('sheets.image.alt_placeholder')}
            className="min-w-0 flex-1 rounded border border-border bg-surface px-2 py-1 text-xs text-text placeholder:text-text-muted focus:outline-none"
          />
          <label className="flex items-center gap-1.5 text-xs text-text-muted">
            {t('sheets.image.width')}
            <input
              value={cell.widthPx ?? ''}
              inputMode="numeric"
              onChange={(event) => setWidth(event.target.value)}
              aria-label={t('sheets.image.width')}
              className="tabular w-16 rounded border border-border bg-surface px-1.5 py-0.5 text-xs text-text focus:outline-none"
            />
          </label>
          <button
            type="button"
            onClick={() => onChange({ ...cell, assetId: null })}
            aria-label={t('sheets.image.remove')}
            className="flex h-9 w-9 items-center justify-center rounded-md text-text-muted hover:text-[var(--status-fail)]"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      )}
    </figure>
  )
}

/**
 * The image cell. Removing an image clears the cell's reference only -- the file
 * stays on the server until the nightly sweep finds that nothing (no working
 * document, no published version) cites it any more. Deleting it here would break
 * undo, and worse, any published version that still shows it (21.3).
 */
export function ImageCellBody({
  cell,
  sheetId,
  readOnly,
  onChange,
}: {
  cell: ImageCell
  /** `null` while the sheet has no server id yet, so nothing can be uploaded. */
  sheetId: number | null
  readOnly: boolean
  onChange: (next: ImageCell) => void
}) {
  const { t } = useTranslation('sheets')
  // Documents saved before assets existed stored `''` here. Anything but a number
  // is "no image".
  const assetId = typeof cell.assetId === 'number' ? cell.assetId : null

  if (assetId !== null) {
    return (
      <StoredImage
        cell={cell}
        sheetId={sheetId}
        assetId={assetId}
        readOnly={readOnly}
        onChange={onChange}
      />
    )
  }
  if (readOnly) return <ImagePlaceholder>{t('sheets.image.empty')}</ImagePlaceholder>
  if (sheetId === null) return <ImagePlaceholder>{t('sheets.image.save_first')}</ImagePlaceholder>
  return (
    <ImageDropZone
      sheetId={sheetId}
      onUploaded={(asset) => onChange({ ...cell, assetId: asset.id })}
    />
  )
}
